import { EXTERNAL_VALUE_UPDATE_TAG } from '#pplx/frontend/composer-sdk/internal/updateTags.js';
import { $getRoot, $isElementNode, getTextDirection, HISTORY_MERGE_TAG } from 'lexical';
// dir="auto" resolves to LTR when a block has no strong directional character,
// so in RTL locales such blocks (like the empty draft) get RTL pinned while
// blocks with content keep direction null to preserve first-strong dir="auto"
// behavior. In LTR locales an `rtl` pin is stale (left by a live locale switch
// or a draft persisted under RTL) and gets cleared; everything else passes
// through so existing serialized states stay untouched.
function $getExpectedDirection(block, localeDirection) {
    if (localeDirection === 'rtl') {
        return getTextDirection(block.getTextContent()) === null ? 'rtl' : null;
    }
    return block.getDirection() === 'rtl' ? null : block.getDirection();
}
function $shouldClearRootDirection(localeDirection) {
    // A pinned root disables Lexical's dir="auto" on directionless paragraphs.
    // Clear root pins in RTL locales and stale RTL pins in LTR locales, while
    // preserving existing LTR-only drafts.
    const direction = $getRoot().getDirection();
    return direction !== null && (localeDirection === 'rtl' || direction === 'rtl');
}
function $normalizeBlockDirections(localeDirection) {
    if ($shouldClearRootDirection(localeDirection)) {
        $getRoot().setDirection(null);
    }
    for (const child of $getRoot().getChildren()) {
        if ($isElementNode(child) &&
            child.getDirection() !== $getExpectedDirection(child, localeDirection)) {
            child.setDirection($getExpectedDirection(child, localeDirection));
        }
    }
}
/**
 * Keeps top-level block directions consistent with the UI locale, so the
 * caret sits on the correct side of an empty or neutral draft and stale root
 * and block direction pins do not override text direction. Register once per
 * editor; returns an unregister callback.
 */
export const registerBlockDirectionNormalization = (editor, localeDirection) => {
    const normalize = (extraTags = []) => editor.update(() => $normalizeBlockDirections(localeDirection), {
        tag: [HISTORY_MERGE_TAG, ...extraTags],
    });
    // Registration runs on mount or a locale-direction change, with no source
    // update whose tags need forwarding. Leave this pass without an external-value
    // tag so locale-driven draft changes can reach onValueChange.
    normalize();
    // An update listener rather than a node transform: transforms do not run on
    // setEditorState (draft restores, controlled-value syncs) and can hit a
    // frozen node map when the replacement is nested in an outer update.
    return editor.registerUpdateListener(({ editorState, tags }) => {
        if (editor.isComposing()) {
            return;
        }
        const needsNormalization = editorState.read(() => $shouldClearRootDirection(localeDirection) ||
            $getRoot()
                .getChildren()
                .some((child) => $isElementNode(child) &&
                child.getDirection() !== $getExpectedDirection(child, localeDirection)));
        if (needsNormalization) {
            // Keep a controlled-value sync's tag on the direction-only follow-up so
            // OnValueChangePlugin does not report it as a user edit.
            normalize(tags.has(EXTERNAL_VALUE_UPDATE_TAG) ? [EXTERNAL_VALUE_UPDATE_TAG] : []);
        }
    });
};
//# sourceMappingURL=blockDirectionNormalization.js.map